/**
 * smoode-parallax.js
 * Parallax & entrance animation engine for the Smoode homepage.
 *
 * Architecture:
 *  - One IntersectionObserver for all entrance animations
 *  - One rAF loop for all continuous scroll effects
 *  - Section-specific handlers (hero, header, announcement bar, HIW tabs, footer)
 *  - Data-attribute API: data-parallax, data-parallax-scroll, data-parallax-speed, etc.
 *  - Zero external dependencies — vanilla JS only
 */
(function () {
  'use strict';

  /* ================================================================
     GUARDS
     ================================================================ */

  /* Respect prefers-reduced-motion */
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /* Merchant has disabled parallax globally */
  if (document.body.getAttribute('data-parallax-enabled') !== 'true') return;

  /* ================================================================
     CONFIG
     ================================================================ */

  const MOBILE_BP = 768; /* entrance animations run on all sizes; scroll parallax desktop-only */

  /* Intensity multiplier from Shopify setting (1–10 → 0.2–2.0) */
  const rawIntensity = parseFloat(document.body.getAttribute('data-parallax-intensity') || '5');
  const INTENSITY = rawIntensity / 5; /* 5 = 1.0× baseline */

  const isDesktop = () => window.innerWidth >= MOBILE_BP;

  /* ================================================================
     SHARED STATE
     ================================================================ */

  let scrollY   = window.scrollY;
  let rafPending = false;
  let resizeTimer = null;

  /* Registered continuous-parallax elements */
  const scrollEls = [];

  /* ================================================================
     UTILITIES
     ================================================================ */

  /**
   * Apply --parallax-offset CSS variable from data attribute so CSS
   * [data-parallax-offset] elements get the right translateY starting state.
   */
  function applyOffsetVariables() {
    document.querySelectorAll('[data-parallax-offset]').forEach(el => {
      const px = parseInt(el.getAttribute('data-parallax-offset'), 10) || 40;
      el.style.setProperty('--parallax-offset', px + 'px');
    });
  }

  /**
   * Apply --parallax-duration CSS variable from data attribute.
   */
  function applyDurationVariables() {
    document.querySelectorAll('[data-parallax-duration]').forEach(el => {
      const ms = parseInt(el.getAttribute('data-parallax-duration'), 10) || 800;
      el.style.setProperty('--parallax-duration', ms + 'ms');
    });
  }

  /* ================================================================
     ENTRANCE ANIMATIONS  (IntersectionObserver)
     ================================================================ */

  /**
   * Activate entrance animation on an element.
   * Handles: single elements, stagger groups, special triggers.
   */
  function activateEntrance(el) {
    const type    = el.getAttribute('data-parallax');
    const delay   = parseInt(el.getAttribute('data-parallax-delay') || '0', 10);
    const stagger = parseInt(el.getAttribute('data-parallax-stagger') || '120', 10);

    if (type === 'fade-up-stagger') {
      /* Set per-child transition delays */
      Array.from(el.children).forEach((child, i) => {
        child.style.transitionDelay = (delay + i * stagger) + 'ms';
      });
      requestAnimationFrame(() => el.classList.add('parallax-visible'));
    } else {
      if (delay) el.style.setProperty('--parallax-delay', delay + 'ms');
      requestAnimationFrame(() => el.classList.add('parallax-visible'));
    }

    /* ── Special post-entrance triggers ── */

    /* UGC cards: start CSS float after entrance completes */
    if (el.classList.contains('smoode-ugc__card')) {
      const dur = parseInt(el.getAttribute('data-parallax-duration') || '800', 10);
      const entranceDelay = delay + i_of(el) * stagger; /* approximate */
      setTimeout(() => el.classList.add('float-active'), dur + entranceDelay + 50);
    }

    /* Testimonial cards: stagger stars in after entrance starts */
    if (el.classList.contains('smoode-testi__card')) {
      const stars = el.querySelectorAll('.smoode-testi__star');
      stars.forEach((star, i) => {
        setTimeout(() => star.style.transitionDelay = (i * 80) + 'ms', 10);
      });
      setTimeout(() => el.classList.add('stars-visible'), delay + 200);
    }

    /* Newsletter input glow */
    if (el.classList.contains('smoode-footer__input')) {
      setTimeout(() => el.classList.add('is-glowing'), 600);
    }
  }

  /** Get approximate index of el among its parallax siblings */
  function i_of(el) {
    const siblings = el.parentElement
      ? Array.from(el.parentElement.children).filter(c => c.hasAttribute('data-parallax'))
      : [];
    return Math.max(0, siblings.indexOf(el));
  }

  /**
   * Build one IntersectionObserver per unique threshold value found on the page.
   * Falls back to 0.15 when no threshold is specified.
   */
  function buildEntranceObservers() {
    /* Collect all unique thresholds */
    const thresholdMap = new Map(); /* threshold → [el, ...] */

    document.querySelectorAll('[data-parallax]').forEach(el => {
      const t = parseFloat(el.getAttribute('data-parallax-threshold') || '0.15');
      if (!thresholdMap.has(t)) thresholdMap.set(t, []);
      thresholdMap.get(t).push(el);
    });

    thresholdMap.forEach((els, threshold) => {
      const obs = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          const target = entry.target;
          activateEntrance(target);
          const once = target.getAttribute('data-parallax-once') !== 'false';
          if (once) obs.unobserve(target);
        });
      }, {
        threshold,
        rootMargin: '0px 0px -5% 0px',
      });

      els.forEach(el => obs.observe(el));
    });
  }

  /* ================================================================
     CONTINUOUS SCROLL PARALLAX  (requestAnimationFrame loop)
     ================================================================ */

  function gatherScrollElements() {
    scrollEls.length = 0;
    document.querySelectorAll('[data-parallax-scroll]').forEach(el => {
      scrollEls.push({
        el,
        type:      el.getAttribute('data-parallax-scroll'),
        speed:     parseFloat(el.getAttribute('data-parallax-speed') || '0.3'),
        direction: el.getAttribute('data-parallax-direction') || 'up',
      });
    });
  }

  function applyScrollParallax() {
    if (!isDesktop()) return;

    const vh = window.innerHeight;

    scrollEls.forEach(({ el, type, speed, direction }) => {
      const rect = el.getBoundingClientRect();

      /* Skip off-screen elements */
      if (rect.bottom < -200 || rect.top > vh + 200) return;

      /* Center-offset: distance from viewport mid to element mid */
      const centerOffset = rect.top + rect.height * 0.5 - vh * 0.5;

      switch (type) {

        /* Hero background image — scrolls at (speed)× page scroll rate */
        case 'hero-img': {
          const y = scrollY * speed * INTENSITY;
          el.style.transform = `translate3d(0, ${y}px, 0)`;
          break;
        }

        /* Hero text/content — drifts upward opposite to image */
        case 'hero-text': {
          const y = -(scrollY * speed * INTENSITY);
          el.style.transform = `translate3d(0, ${y}px, 0)`;
          break;
        }

        /* Hero overlay — opacity increases as section scrolls out */
        case 'overlay': {
          const progress = Math.max(0, Math.min(1, -rect.top / vh));
          const opacity = 0.34 + progress * 0.21 * INTENSITY;
          el.style.background = `rgba(0,0,0,${Math.min(opacity, 0.7)})`;
          break;
        }

        /* Generic vertical — element moves relative to viewport center */
        case 'vertical': {
          const d = direction === 'up' ? -1 : 1;
          const y = centerOffset * speed * d * INTENSITY;
          el.style.transform = `translate3d(0, ${y}px, 0)`;
          break;
        }

        /* Generic horizontal drift */
        case 'horizontal': {
          const d = direction === 'left' ? -1 : 1;
          const x = centerOffset * speed * d * INTENSITY;
          el.style.transform = `translate3d(${x}px, 0, 0)`;
          break;
        }

        /* Image breathing / zoom on scroll */
        case 'scale': {
          /* progress 0→1 as element moves from bottom to top of viewport */
          const progress = Math.max(0, Math.min(1, (vh - rect.top) / (vh + rect.height)));
          const scale = 1 + progress * 0.06 * INTENSITY;
          el.style.transform = `scale(${Math.min(scale, 1.12)})`;
          break;
        }

        /* Wave SVG horizontal drift */
        case 'wave': {
          const x = centerOffset * speed * INTENSITY * 0.5;
          el.style.transform = `translate3d(${x}px, 0, 0)`;
          break;
        }
      }
    });
  }

  /* ================================================================
     ANNOUNCEMENT BAR — fade + collapse after 100px scroll
     ================================================================ */

  function handleAnnouncementBar() {
    const bar = document.querySelector('.smoode-announcement-bar');
    if (!bar) return;
    bar.classList.toggle('is-hidden', scrollY > 100);
  }

  /* ================================================================
     HEADER — backdrop blur + logo scale
     ================================================================ */

  function handleHeader() {
    const header = document.querySelector('.smoode-header');
    if (!header) return;

    /* Blur / border on scroll past 20px */
    header.classList.toggle('is-scrolled', scrollY > 20);

    /* Logo scale: 1.0 → 0.85 as user scrolls 0→300px (desktop only) */
    if (isDesktop()) {
      const logo = header.querySelector('.smoode-header__logo-link');
      if (logo) {
        const scale = Math.max(0.85, 1 - (Math.min(scrollY, 300) / 300) * 0.15);
        logo.style.transform = `scale(${scale.toFixed(4)})`;
      }
    }
  }

  /* ================================================================
     HOW SMOODE WORKS — tab crossfade (MutationObserver)
     ================================================================ */

  function enhanceHIWTabs() {
    const hiw = document.querySelector('.smoode-hiw');
    if (!hiw) return;

    const panels = hiw.querySelectorAll('.smoode-hiw__panel');
    let isAnimating = false;

    /* Watch for active class changes on each panel */
    const panelObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        const panel = mutation.target;
        const wasActive = mutation.oldValue && mutation.oldValue.includes('smoode-hiw__panel--active');
        const isActive  = panel.classList.contains('smoode-hiw__panel--active');

        if (!wasActive && isActive && !isAnimating) {
          /* Panel just became active — animate in from right */
          isAnimating = true;
          panel.classList.add('is-entering');

          /* Double rAF to ensure browser paints the entering state first */
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              panel.classList.remove('is-entering');
              setTimeout(() => { isAnimating = false; }, 420);
            });
          });
        }

        if (wasActive && !isActive) {
          /* Panel just became inactive — animate out to left */
          panel.classList.add('is-exiting');
          setTimeout(() => panel.classList.remove('is-exiting'), 420);
        }
      });
    });

    panels.forEach(panel => {
      panelObserver.observe(panel, {
        attributes:      true,
        attributeFilter: ['class'],
        attributeOldValue: true,
      });
    });
  }

  /* ================================================================
     RAF SCROLL FRAME — single loop driving all continuous effects
     ================================================================ */

  function tick() {
    rafPending = false;
    handleAnnouncementBar();
    handleHeader();
    applyScrollParallax();
  }

  function onScroll() {
    scrollY = window.scrollY;
    if (!rafPending) {
      rafPending = true;
      requestAnimationFrame(tick);
    }
  }

  /* ================================================================
     RESIZE — re-gather elements (in case of layout shift)
     ================================================================ */

  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      gatherScrollElements();
      applyOffsetVariables();
    }, 200);
  }

  /* ================================================================
     INIT
     ================================================================ */

  function init() {
    applyOffsetVariables();
    applyDurationVariables();
    gatherScrollElements();
    buildEntranceObservers();
    enhanceHIWTabs();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    /* Run one frame immediately to handle header/bar on page load */
    requestAnimationFrame(tick);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
